import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiUser } from 'react-icons/fi';
import { fmtTs, fmtTime } from '../utils/time';
import '../styles/FiveMLogTable.css';

// ─── Table metadata (export cho sidebar) ──────────────────────────────────────
// LƯU Ý: rvn_damage_logs đã bị tách ra khỏi danh sách tab — damage chỉ xem qua
// "Log Người Chơi" (per-player) vì nội dung quá nhiều self-damage gây ồn.
// Data vẫn lưu trong DB, vẫn truy vấn được qua API /api/fivem-logs?table=rvn_damage_logs.
export const TABLE_META = {
    rvn_money_logs:     { label: 'Giao Dịch Tiền', icon: '💰', color: '#34d399', cls: 'tbl-money'     },
    rvn_item_logs:      { label: 'Vật Phẩm',        icon: '📦', color: '#a78bfa', cls: 'tbl-item'      },
    rvn_vehicle_logs:   { label: 'Phương Tiện',     icon: '🚗', color: '#60a5fa', cls: 'tbl-vehicle'   },
    rvn_anticheat_logs: { label: 'Chống Hack',      icon: '🛡️', color: '#fbbf24', cls: 'tbl-anticheat' },
    rvn_explosion_logs: { label: 'Nổ Bom',           icon: '💥', color: '#fb923c', cls: 'tbl-explosion' },
    rvn_action_logs:    { label: 'Hành Động',       icon: '🎬', color: '#9ca3af', cls: 'tbl-action'    },
    rvn_admin_logs:     { label: 'Admin',            icon: '🔧', color: '#e879f9', cls: 'tbl-admin'     },
    rvn_lenh_admin_logs:{ label: 'Lệnh Admin',       icon: '📝', color: '#f0abfc', cls: 'tbl-admin'     },
    rvn_death_logs:     { label: 'Tử Vong',          icon: '💀', color: '#ef4444', cls: 'tbl-death'     },
    rvn_private_logs:   { label: 'Log Private',      icon: '🔒', color: '#fbbf24', cls: 'tbl-private'   },
    rvn_big_transfer_logs: { label: 'Chuyển Tiền Lớn',     icon: '💸', color: '#f97316', cls: 'tbl-big-transfer' },
    rvn_rapid_gain_logs:   { label: 'Tăng Tiền Bất Thường', icon: '⚡', color: '#ef4444', cls: 'tbl-rapid-gain'   },
    rvn_admin_spam_logs:   { label: 'Admin Spam Lệnh',      icon: '🛡️', color: '#dc2626', cls: 'tbl-admin-abuse'  },
    rvn_admin_favor_logs:  { label: 'Thiên Vị Người Chơi',  icon: '🎯', color: '#dc2626', cls: 'tbl-admin-abuse'  },
    rvn_admin_self_logs:   { label: 'Admin Tự Buff',        icon: '🪞', color: '#dc2626', cls: 'tbl-admin-abuse'  },
    rvn_modshift_logs:     { label: 'Lịch Trực Mod',        icon: '📋', color: '#10b981', cls: 'tbl-modshift'     },
};

// fmtTs imported from utils/time — luôn render ở múi giờ VN (+7)

const fmtMoney = (n) => {
    if (n == null || isNaN(n)) return '—';
    return '$' + parseFloat(n).toLocaleString('vi-VN');
};

// ─── Player name button (navigates to FiveM player profile) ───────────────────
// Lookup priority cho persistent identifier (chống nhảy nhầm profile):
//   1. citizenId thật (KIZ46181 — bắt đầu bằng chữ, persistent qua mọi session)
//   2. PID (315 — persistent ở Guild 2 logs join/leave/damage)
//   3. name (PlayerSnapshot.name = exact)
//   4. sid:NNN — last resort, server slot ID, có thể trùng
// KHÔNG bao giờ truyền raw "ID:NNN" vì backend sẽ regex match nhầm player khác.
const PlayerBtn = ({ citizenId, name, navigate, serverId, pid, isAdmin, adminLevel }) => {
    if (!name && !citizenId && !serverId && !pid) return <span className="flt-muted">—</span>;

    // Strip "ID:" / "PID:" / "sid:" / "cid:" / "steam:" prefixes — chúng không phải citizenId thật
    // mà chỉ là alias trỏ vào serverId tạm thời (slot có thể bị tái dùng → click sai người).
    const cidStr  = citizenId ? String(citizenId) : '';
    const cidClean = cidStr
        && /^[A-Za-z]/.test(cidStr)
        && !/^(ID|PID|sid|cid|name|steam|fivem|discord):/i.test(cidStr)
        ? cidStr : null;

    let navTarget = null;
    if (cidClean)         navTarget = cidClean;
    else if (pid)         navTarget = `pid:${pid}`;
    else if (name)        navTarget = `name:${name}`;
    else if (serverId)    navTarget = `sid:${serverId}`;
    const adminLabel = adminLevel === 'god' ? 'GOD' : 'ADMIN';
    return (
        <button
            className={`flt-player-btn ${isAdmin ? 'is-admin' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                if (navTarget) navigate(`/fivem-player/${encodeURIComponent(navTarget)}`);
            }}
            title={[
                citizenId,
                serverId && `ID:${serverId}`,
                pid && `PID:${pid}`,
                isAdmin && `Quyền ${adminLabel}`,
            ].filter(Boolean).join(' · ')}
        >
            <FiUser className="flt-player-icon" />
            <span className="flt-player-name">{name || citizenId || (serverId && `#${serverId}`) || '?'}</span>
            {isAdmin && <span className={`flt-admin-badge admin-${adminLevel || 'admin'}`}>{adminLabel}</span>}
            {cidClean && <span className="flt-cid-badge">{cidClean}</span>}
            {serverId && <span className="flt-id-badge">ID:{serverId}</span>}
            {pid      && <span className="flt-pid-badge">PID:{pid}</span>}
        </button>
    );
};

// ─── Money row ────────────────────────────────────────────────────────────────
const MoneyRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const isAdd = String(d.type || '').toLowerCase() === 'add';
    const ts = fmtTs(log.timestamp);
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-badge tbl-money`}>💰 Tiền</span></td>
            <td><PlayerBtn citizenId={d.citizenid || log.citizenId} name={d.name || log.playerName} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td><span className={`flt-amount ${isAdd ? 'amount-add' : 'amount-remove'}`}>{isAdd ? '+' : '-'}{fmtMoney(d.amount)}</span></td>
            <td className="flt-reason">
                {d.counterparty
                    ? <><span style={{ color: '#fbbf24', marginRight: 6 }}>{isAdd ? '←' : '→'}</span><PlayerBtn citizenId={d.counterparty.citizenId} name={d.counterparty.name} serverId={d.counterparty.serverId} pid={d.counterparty.pid} isAdmin={d.counterparty.isAdmin} adminLevel={d.counterparty.adminLevel} navigate={navigate} /></>
                    : (
                        <>
                            <span>{d.reason || d.addinfo || '—'}</span>
                            {d.purchasedItem && (
                                <span className="flt-purchased-item" title="Vật phẩm nhận được trong giao dịch">
                                    {' '}→ 📦 x{d.purchasedItem.count} <span className="flt-purchased-name">{d.purchasedItem.name}</span>
                                </span>
                            )}
                        </>
                    )}
            </td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-badge-type ${isAdd ? 'type-add' : 'type-remove'}`}>{isAdd ? '+ Cộng' : '- Trừ'}</span></td>
            <td><PlayerBtn citizenId={d.citizenid || log.citizenId} name={d.name || log.playerName} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td className="flt-cid">{d.citizenid || log.citizenId || '—'}</td>
            <td><span className={`flt-amount ${isAdd ? 'amount-add' : 'amount-remove'}`}>{isAdd ? '+' : '-'}{fmtMoney(d.amount)}</span></td>
            <td className="flt-reason" title={d.reason || d.addinfo || ''}>
                {d.counterparty
                    ? <>
                        <span style={{ color: '#fbbf24', marginRight: 6 }}>{isAdd ? '←' : '→'}</span>
                        <PlayerBtn
                            citizenId={d.counterparty.citizenId}
                            name={d.counterparty.name}
                            serverId={d.counterparty.serverId}
                            pid={d.counterparty.pid}
                            navigate={navigate}
                        />
                      </>
                    : (
                        <>
                            <span>{d.reason || d.addinfo || '—'}</span>
                            {d.purchasedItem && (
                                <span className="flt-purchased-item" title="Vật phẩm nhận được trong giao dịch">
                                    {' '}→ 📦 x{d.purchasedItem.count} <span className="flt-purchased-name">{d.purchasedItem.name}</span>
                                </span>
                            )}
                        </>
                    )}
            </td>
        </>
    );
};

// ─── Helper: nhãn cho transferType (cốp / drop / stash / glovebox) ────────────
const TRANSFER_LABELS = {
    trunk_in:     { text: '🚗 BỎ VÀO CỐP',     cls: 'type-trunk-in'   },
    trunk_out:    { text: '🚗 LẤY RA CỐP',     cls: 'type-trunk-out'  },
    drop:         { text: '⬇️ VỨT XUỐNG ĐẤT',  cls: 'type-drop'       },
    pickup_drop:  { text: '⬆️ NHẶT TỪ ĐẤT',    cls: 'type-pickup'     },
    glovebox_in:  { text: '🧰 BỎ HỘC XE',      cls: 'type-glove-in'   },
    glovebox_out: { text: '🧰 LẤY HỘC XE',     cls: 'type-trunk-out'  },
    stash_in:     { text: '🗃️ BỎ TỦ',          cls: 'type-trunk-in'   },
    stash_out:    { text: '🗃️ LẤY TỦ',         cls: 'type-trunk-out'  },
    inventory:    { text: '🔄 CHUYỂN',         cls: 'type-swap'       },
};

// ─── Sub-row: hiển thị give_item (Cho Item) với 2 player + ID/PID ─────────────
const GiveItemCells = ({ log, d, navigate, mode }) => {
    const ts = fmtTs(log.timestamp);
    const g = d.giver || {};
    const r = d.receiver || {};
    const itemStr = `${d.item || '?'}${d.count > 1 ? ` ×${d.count}` : ''}`;

    const PlayerWithIds = ({ p, isReceiver }) => (
        <span className={`flt-give-player ${isReceiver ? 'flt-give-receiver' : 'flt-give-giver'}`}>
            <PlayerBtn name={p.name} serverId={p.id} pid={p.pid} isAdmin={p.isAdmin} adminLevel={p.adminLevel} navigate={navigate} />
        </span>
    );

    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-item">🎁 Cho Item</span></td>
            <td><PlayerWithIds p={g} /></td>
            <td className="flt-give-flow">
                <span className="flt-give-arrow">→</span>
                <PlayerWithIds p={r} isReceiver />
                <span className="flt-give-item">{itemStr}</span>
            </td>
            <td className="flt-reason">{d.reason || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge-type type-give">🎁 ĐƯA</span></td>
            <td><PlayerWithIds p={g} /></td>
            <td className="flt-give-flow"><span className="flt-give-arrow">→</span><PlayerWithIds p={r} isReceiver /></td>
            <td><span className="flt-item-name">{itemStr}</span></td>
            <td className="flt-center">{d.count || 1}</td>
            <td className="flt-reason">{d.reason || '—'}</td>
        </>
    );
};

// ─── Sub-row: swap với trunk/drop/stash ───────────────────────────────────────
const SwapCells = ({ log, d, navigate, mode }) => {
    const ts = fmtTs(log.timestamp);
    const lbl = TRANSFER_LABELS[d.transferType] || TRANSFER_LABELS.inventory;
    const itemStr = `${d.item || '?'}${d.count > 1 ? ` ×${d.count}` : ''}`;
    // Chỗ hiện cốp/drop nếu có
    const containerInfo = d.transferType?.startsWith('trunk')
        ? (d.transferType === 'trunk_in' ? d.to : d.from)
        : (d.from && /^(drop|stash|glove)/i.test(d.from)) ? d.from
        : (d.to   && /^(drop|stash|glove)/i.test(d.to))   ? d.to
        : null;

    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-item">📦 Vật phẩm</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} serverId={d.serverId} pid={d.pid} navigate={navigate} /></td>
            <td>
                <span className={`flt-badge-type ${lbl.cls}`}>{lbl.text}</span>{' '}
                <span className="flt-item-name">{itemStr}</span>
                {containerInfo && <span className="flt-container">({containerInfo})</span>}
            </td>
            <td className="flt-reason">{d.reason || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-badge-type ${lbl.cls}`}>{lbl.text}</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} serverId={d.serverId} pid={d.pid} navigate={navigate} /></td>
            <td className="flt-cid">
                {d.serverId && <span>ID:{d.serverId}</span>}
                {d.pid && <span className="flt-give-pid">PID:{d.pid}</span>}
            </td>
            <td>
                <span className="flt-item-name">{itemStr}</span>
                {containerInfo && <span className="flt-container">({containerInfo})</span>}
            </td>
            <td className="flt-center">{d.count || 1}</td>
            <td className="flt-reason">{d.reason || '—'}</td>
        </>
    );
};

// ─── Item row (dispatch) ──────────────────────────────────────────────────────
const ItemRow = ({ log, navigate, mode }) => {
    const d = log.data || {};

    if (d.action === 'give' || d.giver || d.receiver) {
        return <GiveItemCells log={log} d={d} navigate={navigate} mode={mode} />;
    }
    if (d.transferType) {
        return <SwapCells log={log} d={d} navigate={navigate} mode={mode} />;
    }

    const ts = fmtTs(log.timestamp);
    const isAdd = /^add/.test(String(d.action || '').toLowerCase());
    const qty   = d.count ?? d.amount ?? 1;
    const itemName = d.item || d.item_name || '?';
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-item">📦 Vật phẩm</span></td>
            <td><PlayerBtn citizenId={d.citizenid || log.citizenId} name={d.name || log.playerName} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td>
                <span className={`flt-badge-type ${isAdd ? 'type-add' : 'type-remove'}`}>{d.action}</span>
                {' '}<span className="flt-item-name">{itemName}</span>
                <span className="flt-qty"> ×{qty}</span>
            </td>
            <td className="flt-reason">{d.reason || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-badge-type ${isAdd ? 'type-add' : 'type-remove'}`}>{d.action || '—'}</span></td>
            <td><PlayerBtn citizenId={d.citizenid || log.citizenId} name={d.name || log.playerName} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td className="flt-cid">{d.citizenid || log.citizenId || '—'}</td>
            <td><span className="flt-item-name">{itemName}</span></td>
            <td className="flt-center">{qty}</td>
            <td className="flt-reason" title={d.reason || ''}>{d.reason || '—'}</td>
        </>
    );
};

// ─── Admin row ────────────────────────────────────────────────────────────────
const AdminRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const adminName  = d.source || log.playerName;
    const targetName = d.target || log.targetName;
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-admin">🔧 Admin</span></td>
            <td><PlayerBtn name={adminName} citizenId={d.citizenid} serverId={d.serverId} pid={d.pid} navigate={navigate} /></td>
            <td><PlayerBtn name={targetName} citizenId={d.targetCitizenId} serverId={d.targetServerId} pid={d.targetPid} navigate={navigate} /></td>
            <td className="flt-reason">{d.action} {d.reason ? `— ${d.reason}` : ''}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><PlayerBtn name={adminName} citizenId={d.citizenid} serverId={d.serverId} pid={d.pid} navigate={navigate} /></td>
            <td><PlayerBtn name={targetName} citizenId={d.targetCitizenId} serverId={d.targetServerId} pid={d.targetPid} navigate={navigate} /></td>
            <td className="flt-action" title={d.action || ''}>{d.action || '—'}</td>
            <td className="flt-reason" title={d.reason || ''}>{d.reason || '—'}</td>
        </>
    );
};

// ─── Lệnh Admin row (RVN City - coin/permission/unban) ────────────────────────
const CATEGORY_BADGE = {
    coin:       { text: '🪙 COIN',     cls: 'type-add'   },
    permission: { text: '🛡️ QUYỀN',    cls: 'type-give'  },
    unban:      { text: '🔓 UNBAN',    cls: 'type-pickup'},
    item:       { text: '📦 ITEM',     cls: 'type-swap'  },
    thao_tac:   { text: '🔧 THAO TÁC', cls: 'type-swap'  },
    other:      { text: '📝 KHÁC',     cls: 'type-swap'  },
};

// Render giá trị giàu thông tin: số coin lớn, hoặc → quyền god/admin, hoặc item name
const ValueCell = ({ d }) => {
    if (d.permission) return (
        <span className="flt-perm-grant">
            <span style={{ color: '#fbbf24' }}>→</span>
            <span className={`flt-perm-badge perm-${d.permission.toLowerCase()}`}>{d.permission.toUpperCase()}</span>
        </span>
    );
    if (d.amount != null) return (
        <span className="flt-coin-amt">
            <span className="flt-amt-num">+{d.amount.toLocaleString('vi-VN')}</span>
            <span className="flt-amt-unit">coin</span>
        </span>
    );
    if (d.item) return <span className="flt-item-name">📦 {d.item}</span>;
    return <span className="flt-muted">—</span>;
};

const LenhAdminRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const cat = CATEGORY_BADGE[d.category] || CATEGORY_BADGE.other;

    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-admin">📝 Lệnh Admin</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td>
                <span className={`flt-badge-type ${cat.cls}`}>{cat.text}</span>{' '}
                <ValueCell d={d} />
            </td>
            <td className="flt-reason">{d.operation || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-badge-type ${cat.cls}`}>{cat.text}</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td className="flt-cid">{d.citizenid || '—'}</td>
            <td className="flt-action" title={d.operation || ''}>{d.operation || '—'}</td>
            <td><ValueCell d={d} /></td>
        </>
    );
};

// ─── Damage row ───────────────────────────────────────────────────────────────
const DamageRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const src = d.source_name || log.playerName || (d.source != null ? `ID ${d.source}` : '?');
    const tgt = d.target_name || log.targetName || (d.target != null ? `ID ${d.target}` : '?');
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-damage">⚔️ Sát thương</span></td>
            <td><span className="flt-name">{src}</span></td>
            <td><span className="flt-name flt-killer">{tgt}</span></td>
            <td>{d.weapon || '—'} {d.damage != null ? <span className="flt-dmg-val">{d.damage} dmg</span> : ''}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td className="flt-name">{src}</td>
            <td className="flt-name flt-killer">{tgt}</td>
            <td className="flt-weapon">{d.weapon || '—'}</td>
            <td>{d.damage != null ? <span className="flt-dmg-val">{d.damage}</span> : '—'}</td>
        </>
    );
};

// ─── Death row ────────────────────────────────────────────────────────────────
const DeathRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const player = d.player_name || log.playerName || (d.player_id || d.player ? `ID ${d.player_id || d.player}` : '?');
    const killer = d.killer_name || log.targetName || (d.killer_id || d.killer ? `ID ${d.killer_id || d.killer}` : null);
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-death">💀 Tử vong</span></td>
            <td><span className="flt-name">{player}</span></td>
            <td><span className={killer ? 'flt-name flt-killer' : 'flt-muted'}>{killer || '— (tự chết)'}</span></td>
            <td className="flt-cause">{d.cause || d.weapon || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td className="flt-name">{player}</td>
            <td className={killer ? 'flt-name flt-killer' : 'flt-muted'}>{killer || '— (tự chết)'}</td>
            <td className="flt-cause">{d.cause || d.weapon || '—'}</td>
        </>
    );
};

// ─── Vehicle row ──────────────────────────────────────────────────────────────
const VehicleRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-vehicle">🚗 Xe</span></td>
            <td><PlayerBtn citizenId={d.citizenid || log.citizenId} name={d.name || log.playerName} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td className="flt-reason">{d.channelLabel || d.action || '—'}</td>
            <td className="flt-reason">{d.reason || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge-type type-add">{d.action || d.channelLabel || '—'}</span></td>
            <td><PlayerBtn citizenId={d.citizenid || log.citizenId} name={d.name || log.playerName} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td className="flt-cid">{d.citizenid || log.citizenId || '—'}</td>
            <td className="flt-reason" title={d.reason || ''}>{d.reason || '—'}</td>
        </>
    );
};

// ─── Anticheat row ────────────────────────────────────────────────────────────
const AnticheatRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const playerName = d.source || log.playerName;
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-anticheat">🛡️ AC</span></td>
            <td><PlayerBtn name={playerName} citizenId={d.citizenid || log.citizenId} serverId={d.serverId} pid={d.pid} navigate={navigate} /></td>
            <td className="flt-reason">{d.reason || '—'}</td>
            <td className="flt-cid">{d.channelLabel || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><PlayerBtn name={playerName} citizenId={d.citizenid || log.citizenId} serverId={d.serverId} pid={d.pid} navigate={navigate} /></td>
            <td className="flt-cid">{d.citizenid || log.citizenId || '—'}</td>
            <td><span className="flt-badge-type type-remove">{d.action || 'Detection'}</span></td>
            <td className="flt-reason" title={d.reason || ''}>{d.reason || '—'}</td>
        </>
    );
};

// ─── Action row (generic) ─────────────────────────────────────────────────────
const ActionRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-action">🎬 Hành động</span></td>
            <td><PlayerBtn citizenId={d.citizenid || log.citizenId} name={d.name || log.playerName} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td className="flt-action">{d.channelLabel || d.action || '—'}</td>
            <td className="flt-reason">{d.reason || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><PlayerBtn citizenId={d.citizenid || log.citizenId} name={d.name || log.playerName} serverId={d.serverId} pid={d.pid} isAdmin={d.isAdmin} adminLevel={d.adminLevel} navigate={navigate} /></td>
            <td className="flt-cid">{d.citizenid || log.citizenId || '—'}</td>
            <td className="flt-action">{d.action || d.channelLabel || '—'}</td>
            <td className="flt-reason" title={d.reason || ''}>{d.reason || '—'}</td>
        </>
    );
};

// ─── Big Transfer alert row ───────────────────────────────────────────────────
const BigTransferRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const sevClass = `flt-sev-${d.severity || 'low'}`;
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-big-transfer">💸 GD lớn</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} navigate={navigate} /></td>
            <td><span className={`flt-amount ${d.transactionType === 'add' ? 'amount-add' : 'amount-remove'}`}>
                {d.transactionType === 'add' ? '+' : '-'}{fmtMoney(d.amount)} <small className="flt-cur">{d.currency}</small>
            </span></td>
            <td className="flt-reason" title={d.reason}>{d.reason || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-sev-badge ${sevClass}`}>{(d.severity || 'low').toUpperCase()}</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} navigate={navigate} /></td>
            <td>
                <span className={`flt-amount ${d.transactionType === 'add' ? 'amount-add' : 'amount-remove'}`}>
                    {d.transactionType === 'add' ? '+' : '-'}{fmtMoney(d.amount)}
                </span>
                <span className="flt-cur"> {d.currency}</span>
                {d.isTransfer && <span className="flt-transfer-tag" title="Khớp pattern chuyển khoản"> 🔄</span>}
            </td>
            <td className="flt-cid">≥ {fmtMoney(d.threshold)}</td>
            <td className="flt-reason" title={d.reason}>{d.reason || '—'}</td>
        </>
    );
};

// ─── Rapid Gain alert row ─────────────────────────────────────────────────────
const RapidGainRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const sevClass = `flt-sev-${d.severity || 'low'}`;
    const winMin = d.timeWindowSec ? Math.round(d.timeWindowSec / 60) : '?';
    const events = Array.isArray(d.events) ? d.events : [];

    // Group events by currency
    const byCurrency = {};
    const reasonCounts = {};
    let cashTotal = 0, bankTotal = 0, cryptoTotal = 0;
    for (const ev of events) {
        const cur = (ev.currency || 'cash').toLowerCase();
        byCurrency[cur] = (byCurrency[cur] || 0) + 1;
        if (cur === 'cash') cashTotal += Math.abs(ev.amount || 0);
        else if (cur === 'bank') bankTotal += Math.abs(ev.amount || 0);
        else if (cur === 'crypto') cryptoTotal += Math.abs(ev.amount || 0);
        if (ev.reason) reasonCounts[ev.reason] = (reasonCounts[ev.reason] || 0) + 1;
    }
    const topReason = Object.entries(reasonCounts).sort((a,b) => b[1] - a[1])[0];

    // Render currency mix: "3× cash + 2× bank"
    const currencyMix = Object.entries(byCurrency)
        .map(([cur, n]) => `${n}× ${cur}`).join(' + ');

    // Render breakdown by currency: "$15K cash · $3K bank"
    const breakdown = [];
    if (cashTotal)   breakdown.push(<span key="c" className="flt-bd flt-bd-cash">{fmtMoney(cashTotal)} <small>cash</small></span>);
    if (bankTotal)   breakdown.push(<span key="b" className="flt-bd flt-bd-bank">{fmtMoney(bankTotal)} <small>bank</small></span>);
    if (cryptoTotal) breakdown.push(<span key="x" className="flt-bd flt-bd-crypto">{fmtMoney(cryptoTotal)} <small>crypto</small></span>);

    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-rapid-gain">⚡ Tăng nhanh</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} navigate={navigate} /></td>
            <td>
                <span className="flt-amount amount-add">+{fmtMoney(d.totalAmount)}</span>{' '}
                <small className="flt-cur">({d.eventCount} lần · {currencyMix})</small>
            </td>
            <td className="flt-reason" title={topReason ? `${topReason[0]} (×${topReason[1]})` : ''}>
                {topReason ? `${topReason[0]} ×${topReason[1]}` : '—'}
            </td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-sev-badge ${sevClass}`}>{(d.severity || 'low').toUpperCase()}</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} navigate={navigate} /></td>
            <td>
                <div className="flt-cur-mix">
                    <span className="flt-event-count">{d.eventCount}× +tiền</span>
                    <span className="flt-cur-detail">{currencyMix}</span>
                </div>
            </td>
            <td className="flt-bd-list">{breakdown.length ? breakdown : <span className="flt-amount amount-add">+{fmtMoney(d.totalAmount)}</span>}</td>
            <td className="flt-reason" title={topReason ? `${topReason[0]} (xuất hiện ${topReason[1]} lần)` : ''}>
                {topReason ? <><strong>{topReason[0]}</strong> <small>×{topReason[1]}</small></> : '—'}
            </td>
            <td className="flt-cid">{winMin} phút</td>
        </>
    );
};

// ─── Admin Spam row (1 admin spam cùng 1 lệnh) ───────────────────────────────
const AdminSpamRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const sevClass = `flt-sev-${d.severity || 'low'}`;
    const winMin = d.timeWindowSec ? Math.round(d.timeWindowSec / 60) : '?';
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-admin-abuse">🛡️ Spam</span></td>
            <td><PlayerBtn citizenId={d.adminCitizenId} name={d.adminName} navigate={navigate} /></td>
            <td><span className="flt-cmd">{d.command || '?'}</span> <small className="flt-cur">×{d.eventCount}</small></td>
            <td className="flt-reason">trong {winMin} phút</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-sev-badge ${sevClass}`}>{(d.severity || 'low').toUpperCase()}</span></td>
            <td><PlayerBtn citizenId={d.adminCitizenId} name={d.adminName} navigate={navigate} /></td>
            <td><span className="flt-cmd">{d.command || '?'}</span></td>
            <td className="flt-center"><span className="flt-event-count">{d.eventCount}× lần</span></td>
            <td className="flt-cid">{winMin} phút</td>
        </>
    );
};

// ─── Admin Favoritism row ────────────────────────────────────────────────────
const AdminFavorRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const sevClass = `flt-sev-${d.severity || 'low'}`;
    const winMin = d.timeWindowSec ? Math.round(d.timeWindowSec / 60) : '?';
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-admin-abuse">🎯 Thiên vị</span></td>
            <td><PlayerBtn citizenId={d.targetCitizenId} name={d.targetName} navigate={navigate} /></td>
            <td>{d.eventCount}× từ {d.distinctAdmins} admin</td>
            <td className="flt-reason">{winMin} phút</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-sev-badge ${sevClass}`}>{(d.severity || 'low').toUpperCase()}</span></td>
            <td><PlayerBtn citizenId={d.targetCitizenId} name={d.targetName} navigate={navigate} /></td>
            <td className="flt-center"><span className="flt-event-count">{d.eventCount}× actions</span></td>
            <td className="flt-center">{d.distinctCommands || 1} lệnh khác nhau</td>
            <td className="flt-center">{d.distinctAdmins > 1 ? <span className="flt-killer">{d.distinctAdmins} admin</span> : '1 admin'}</td>
            <td className="flt-cid">{winMin} phút</td>
        </>
    );
};

// ─── Admin Self-Use row ──────────────────────────────────────────────────────
const AdminSelfRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const sevClass = `flt-sev-${d.severity || 'low'}`;
    const winMin = d.timeWindowSec ? Math.round(d.timeWindowSec / 60) : '?';
    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-admin-abuse">🪞 Tự buff</span></td>
            <td><PlayerBtn citizenId={d.adminCitizenId} name={d.adminName} navigate={navigate} /></td>
            <td>{d.eventCount}× {d.command ? `(${d.command})` : `(${d.distinctCommands} lệnh)`}</td>
            <td className="flt-reason">{winMin} phút</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className={`flt-sev-badge ${sevClass}`}>{(d.severity || 'low').toUpperCase()}</span></td>
            <td><PlayerBtn citizenId={d.adminCitizenId} name={d.adminName} navigate={navigate} /></td>
            <td className="flt-center"><span className="flt-event-count">{d.eventCount}× lần</span></td>
            <td>{d.command ? <span className="flt-cmd">{d.command}</span> : <span>{d.distinctCommands} lệnh khác nhau</span>}</td>
            <td className="flt-cid">{winMin} phút</td>
        </>
    );
};

// ─── Mod Shift row ────────────────────────────────────────────────────────────
const ModShiftRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const startedAt = new Date(d.startedAt);
    const endedAt = d.endedAt ? new Date(d.endedAt) : null;

    // Live elapsed time cho active shifts
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!d.isActive) return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [d.isActive]);

    const liveDurSec = d.isActive
        ? Math.floor((now - startedAt.getTime()) / 1000)
        : Math.round((d.durationMinutes ?? 0) * 60);
    const liveDurMin = Math.floor(liveDurSec / 60);
    const fmtDur = (m) => {
        if (m < 60) return `${m} phút`;
        const h = Math.floor(m/60), r = m % 60;
        return r === 0 ? `${h} giờ` : `${h}h ${r}p`;
    };
    const fmtDurLive = (sec) => {
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        const s = sec % 60;
        return h > 0 ? `${h}h ${m}p ${s}s` : `${m}p ${s}s`;
    };

    const permEmoji = d.permission === 'god' ? '👑' : d.permission === 'admin' ? '🛡️' : '🔧';
    const permClass = `flt-perm-${d.permission}`;

    const reasonLabel = {
        offline:  '🔌 Offline',
        timeout:  '⏱ Quá 24h',
        replaced: '🔁 Cấp lại',
        manual:   '✋ Thủ công',
    }[d.endReason] || (d.isActive ? null : (d.endReason || '—'));

    const startFmt = startedAt ? fmtTs(startedAt) : null;
    const endFmt = endedAt ? fmtTs(endedAt) : null;

    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-modshift">📋 Ca trực</span></td>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} navigate={navigate} /></td>
            <td>{permEmoji} {(d.permission || '').toUpperCase()} · <strong>{fmtDur(liveDurMin)}</strong></td>
            <td className="flt-reason">{d.isActive ? <span className="flt-shift-active">🟢 Đang trực</span> : reasonLabel}</td>
        </>
    );
    return (
        <>
            <td><PlayerBtn citizenId={d.citizenid} name={d.name} navigate={navigate} /></td>
            <td><span className={`flt-perm-badge ${permClass}`}>{permEmoji} {(d.permission || '?').toUpperCase()}</span></td>
            <td className="flt-time-cell">
                <span className="flt-t">{startFmt.time}</span>
                <span className="flt-d">{startFmt.date}</span>
            </td>
            <td className="flt-time-cell">
                {d.isActive
                    ? (() => {
                        const nowFmt = fmtTs(new Date(now));
                        return (
                            <>
                                <span className="flt-t flt-t-live">{nowFmt.time}</span>
                                <span className="flt-d flt-d-live">{nowFmt.date} · live</span>
                            </>
                        );
                      })()
                    : endFmt
                        ? <><span className="flt-t">{endFmt.time}</span><span className="flt-d">{endFmt.date}</span></>
                        : <span className="flt-muted">—</span>
                }
            </td>
            <td>
                <span className={`flt-shift-dur ${d.isActive ? 'is-live' : ''}`}>
                    {d.isActive
                        ? <><strong>{fmtDurLive(liveDurSec)}</strong><small> · live</small></>
                        : <strong>{fmtDur(liveDurMin)}</strong>
                    }
                </span>
            </td>
            <td>
                {d.isActive
                    ? <span className="flt-shift-active">🟢 ĐANG TRỰC</span>
                    : <span className="flt-shift-ended">🔴 Hết ca</span>}
            </td>
            <td className="flt-cid">{d.grantedBy || '—'}</td>
            <td className="flt-reason">{reasonLabel || '—'}</td>
        </>
    );
};

// ─── Private (admin command) row ──────────────────────────────────────────────
const PrivateRow = ({ log, navigate, mode }) => {
    const d = log.data || {};
    const ts = fmtTs(log.timestamp);
    const adm = d.admin || {};
    const tgt = d.target || {};
    const pos = d.position;

    const PersonBadge = ({ p, label, className }) => {
        if (!p?.citizenId && !p?.name) return <span className="flt-muted">—</span>;
        return (
            <button
                className={`flt-player-btn ${className || ''}`}
                onClick={(e) => { e.stopPropagation(); if (p.citizenId) navigate(`/fivem-player/${encodeURIComponent(p.citizenId)}`); }}
                title={`${p.role || label || ''}: ${p.name || ''} (${p.citizenId || ''})`}
            >
                <FiUser className="flt-player-icon" />
                <span className="flt-player-name">{p.name || p.citizenId}</span>
                {p.citizenId && <span className="flt-cid-badge">{p.citizenId}</span>}
            </button>
        );
    };

    const PosCell = pos ? (
        <td className="flt-pos" title={`vec3(${pos.x}, ${pos.y}, ${pos.z})`}>
            <span className="flt-pos-coord">x:{pos.x}</span>
            <span className="flt-pos-coord">y:{pos.y}</span>
            <span className="flt-pos-coord">z:{pos.z}</span>
        </td>
    ) : <td className="flt-muted">—</td>;

    if (mode === 'all') return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-badge tbl-private">🔒 {d.command || 'Private'}</span></td>
            <td><PersonBadge p={adm} label="Admin" /></td>
            <td className="flt-reason" title={d.reason || ''}>{d.reason || '—'}</td>
        </>
    );
    return (
        <>
            <td className="flt-time-cell"><span className="flt-t">{ts.time}</span><span className="flt-d">{ts.date}</span></td>
            <td><span className="flt-cmd">{d.command || '—'}</span></td>
            <td><PersonBadge p={adm} label="Admin" /></td>
            <td><PersonBadge p={tgt} label="Target" className="flt-target-btn" /></td>
            {PosCell}
            <td className="flt-reason" title={d.reason || ''}>{d.reason || '—'}</td>
        </>
    );
};

// ─── Single log row ───────────────────────────────────────────────────────────
const FiveMLogRow = ({ log, mode }) => {
    const [exp, setExp] = useState(false);
    const navigate = useNavigate();
    const meta = TABLE_META[log.table] || { label: log.table, cls: '' };

    const rowClass = [
        'flt-row',
        log.table === 'rvn_damage_logs' ? 'row-damage' : '',
        log.table === 'rvn_death_logs'  ? 'row-death'  : '',
        log.table === 'rvn_admin_logs'  ? 'row-admin'  : '',
        exp ? 'row-exp' : '',
    ].filter(Boolean).join(' ');

    const cells = (() => {
        switch (log.table) {
            case 'rvn_money_logs':  return <MoneyRow  log={log} navigate={navigate} mode={mode} />;
            case 'rvn_item_logs':   return <ItemRow   log={log} navigate={navigate} mode={mode} />;
            case 'rvn_admin_logs':  return <AdminRow  log={log} navigate={navigate} mode={mode} />;
            case 'rvn_lenh_admin_logs': return <LenhAdminRow log={log} navigate={navigate} mode={mode} />;
            case 'rvn_damage_logs':    return <DamageRow    log={log} navigate={navigate} mode={mode} />;
            case 'rvn_death_logs':     return <DeathRow     log={log} navigate={navigate} mode={mode} />;
            case 'rvn_vehicle_logs':   return <VehicleRow   log={log} navigate={navigate} mode={mode} />;
            case 'rvn_anticheat_logs': return <AnticheatRow log={log} navigate={navigate} mode={mode} />;
            case 'rvn_explosion_logs': return <ActionRow    log={log} navigate={navigate} mode={mode} />;
            case 'rvn_action_logs':    return <ActionRow    log={log} navigate={navigate} mode={mode} />;
            case 'rvn_private_logs':       return <PrivateRow     log={log} navigate={navigate} mode={mode} />;
            case 'rvn_big_transfer_logs':  return <BigTransferRow log={log} navigate={navigate} mode={mode} />;
            case 'rvn_rapid_gain_logs':    return <RapidGainRow   log={log} navigate={navigate} mode={mode} />;
            case 'rvn_admin_spam_logs':    return <AdminSpamRow   log={log} navigate={navigate} mode={mode} />;
            case 'rvn_admin_favor_logs':   return <AdminFavorRow  log={log} navigate={navigate} mode={mode} />;
            case 'rvn_admin_self_logs':    return <AdminSelfRow   log={log} navigate={navigate} mode={mode} />;
            case 'rvn_modshift_logs':      return <ModShiftRow    log={log} navigate={navigate} mode={mode} />;
            default:
                const ts2 = fmtTs(log.timestamp);
                return (
                    <>
                        <td className="flt-time-cell"><span className="flt-t">{ts2.time}</span><span className="flt-d">{ts2.date}</span></td>
                        {mode === 'all' && <td><span className={`flt-badge ${meta.cls}`}>{meta.label}</span></td>}
                        <td colSpan={3} className="flt-muted">{JSON.stringify(log.data).slice(0, 120)}</td>
                    </>
                );
        }
    })();

    // Count non-null data fields for expand button
    const dataKeys = Object.keys(log.data || {}).filter(k => log.data[k] != null).length;

    return (
        <>
            <tr className={rowClass} onClick={() => dataKeys > 0 && setExp(v => !v)}>
                {cells}
                <td className="flt-expand-td">
                    {dataKeys > 0 && (
                        <button className="flt-exp-btn" onClick={e => { e.stopPropagation(); setExp(v => !v); }}>
                            {exp ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                    )}
                </td>
            </tr>
            {exp && (
                <tr className="flt-detail-row">
                    <td colSpan={99}>
                        {/* Custom expand cho admin abuse alerts */}
                        {['rvn_admin_spam_logs','rvn_admin_favor_logs','rvn_admin_self_logs'].includes(log.table) && Array.isArray(log.data?.events) ? (
                            <div className="flt-events-wrap">
                                <div className="flt-events-title">
                                    🛡️ Chi tiết {log.data.events.length} action(s) trong window:
                                </div>
                                <table className="flt-events-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Thời Gian</th>
                                            <th>Lệnh</th>
                                            <th>Admin</th>
                                            <th>Đối Tượng</th>
                                            <th>Vị Trí</th>
                                            <th>Cách trước</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {log.data.events.map((ev, i) => {
                                            const t = new Date(ev.time);
                                            const prevT = i > 0 ? new Date(log.data.events[i-1].time) : null;
                                            const gap = prevT ? Math.round((t - prevT) / 1000) : null;
                                            const adm = ev.admin || {};
                                            const tgt = ev.target || {};
                                            const isSelf = adm.citizenId && adm.citizenId === tgt.citizenId;
                                            return (
                                                <tr key={i}>
                                                    <td className="flt-ev-num">{i + 1}</td>
                                                    <td className="flt-ev-time">{fmtTime(t)}</td>
                                                    <td><span className="flt-cmd">{ev.command || '?'}</span></td>
                                                    <td>{adm.name ? <>{adm.name} <small className="flt-cid">{adm.citizenId}</small></> : '—'}</td>
                                                    <td>
                                                        {tgt.name ? <>{tgt.name} <small className="flt-cid">{tgt.citizenId}</small></> : '—'}
                                                        {isSelf && <span className="flt-self-tag" title="Self use"> 🪞</span>}
                                                    </td>
                                                    <td className="flt-ev-pos">
                                                        {ev.position ? `${ev.position.x.toFixed(0)}, ${ev.position.y.toFixed(0)}` : '—'}
                                                    </td>
                                                    <td className="flt-ev-gap">{gap != null ? `+${gap}s` : '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) :
                        /* Custom expand cho rapid_gain: bảng các event */
                        log.table === 'rvn_rapid_gain_logs' && Array.isArray(log.data?.events) ? (
                            <div className="flt-events-wrap">
                                <div className="flt-events-title">
                                    📋 Chi tiết {log.data.events.length} lần +tiền trong window:
                                </div>
                                <table className="flt-events-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Thời Gian</th>
                                            <th>Số Tiền</th>
                                            <th>Loại Tiền</th>
                                            <th>Lý Do</th>
                                            <th>Cách event trước</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {log.data.events.map((ev, i) => {
                                            const t = new Date(ev.time);
                                            const prevT = i > 0 ? new Date(log.data.events[i-1].time) : null;
                                            const gap = prevT ? Math.round((t - prevT) / 1000) : null;
                                            return (
                                                <tr key={i}>
                                                    <td className="flt-ev-num">{i + 1}</td>
                                                    <td className="flt-ev-time">{fmtTime(t)}</td>
                                                    <td><span className="flt-amount amount-add">+{fmtMoney(ev.amount)}</span></td>
                                                    <td><span className={`flt-bd-pill flt-bd-${ev.currency || 'cash'}`}>{ev.currency || '?'}</span></td>
                                                    <td className="flt-ev-reason">{ev.reason || '—'}</td>
                                                    <td className="flt-ev-gap">{gap != null ? `+${gap}s` : '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="flt-detail-grid">
                                {Object.entries(log.data || {}).map(([k, v]) =>
                                    v != null && String(v).length > 0 ? (
                                        <div key={k} className="flt-kv">
                                            <span className="flt-k">{k}</span>
                                            <span className="flt-v">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                                        </div>
                                    ) : null
                                )}
                            </div>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
};

// ─── Column headers ───────────────────────────────────────────────────────────
const HEADERS = {
    all:                ['Thời Gian', 'Loại', 'Người Chơi', 'Chi Tiết', '', ''],
    rvn_money_logs:     ['Thời Gian', 'Loại GD', 'Người Chơi', 'CitizenID', 'Số Tiền', 'Lý Do'],
    rvn_item_logs:      ['Thời Gian', 'Hành Động', 'Người Chơi', 'CitizenID', 'Vật Phẩm', 'SL', 'Lý Do'],
    rvn_vehicle_logs:   ['Thời Gian', 'Hành Động', 'Người Chơi', 'CitizenID', 'Chi Tiết'],
    rvn_anticheat_logs: ['Thời Gian', 'Người Chơi', 'CitizenID/Steam', 'Phát Hiện', 'Lý Do'],
    rvn_explosion_logs: ['Thời Gian', 'Người Chơi', 'CitizenID', 'Hành Động', 'Chi Tiết'],
    rvn_action_logs:    ['Thời Gian', 'Người Chơi', 'CitizenID', 'Hành Động', 'Chi Tiết'],
    rvn_admin_logs:     ['Thời Gian', 'Admin', 'Đối Tượng', 'Hành Động', 'Lý Do'],
    rvn_lenh_admin_logs:['Thời Gian', 'Loại', 'Người Bị Chỉnh', 'CitizenID', 'Lệnh', 'Giá Trị'],
    rvn_damage_logs:    ['Thời Gian', 'Người Tấn Công', 'Nạn Nhân', 'Vũ Khí', 'Sát Thương'],
    rvn_death_logs:     ['Thời Gian', 'Người Chơi', 'Kẻ Giết', 'Nguyên Nhân'],
    rvn_private_logs:   ['Thời Gian', 'Lệnh', 'Admin Sử Dụng', 'Đối Tượng', 'Vị Trí', 'Chi Tiết'],
    rvn_big_transfer_logs: ['Thời Gian', 'Mức Độ', 'Người Chơi', 'Số Tiền', 'Ngưỡng', 'Lý Do'],
    rvn_rapid_gain_logs:   ['Thời Gian', 'Mức Độ', 'Người Chơi', 'Loại Tiền', 'Tổng Tiền', 'Lý Do Chính', 'Window'],
    rvn_admin_spam_logs:   ['Thời Gian', 'Mức Độ', 'Admin', 'Lệnh', 'Số Lần', 'Window'],
    rvn_admin_favor_logs:  ['Thời Gian', 'Mức Độ', 'Người Được Ưu Đãi', 'Số Lần', 'Số Lệnh', 'Số Admin', 'Window'],
    rvn_admin_self_logs:   ['Thời Gian', 'Mức Độ', 'Admin Tự Dùng', 'Số Lần', 'Lệnh / Số Lệnh', 'Window'],
    rvn_modshift_logs:     ['Người Trực', 'Quyền', 'Bắt Đầu', 'Kết Thúc', 'Thời Lượng', 'Trạng Thái', 'Người Cấp', 'Lý Do Kết Thúc'],
};

// ─── Main table ───────────────────────────────────────────────────────────────
const FiveMLogTable = ({ logs, loading, pagination, onPageChange, table = 'all' }) => {
    const mode    = table === 'all' ? 'all' : 'specific';
    const headers = (HEADERS[table] || HEADERS.all).filter(Boolean);

    if (loading) return (
        <div className="flt-state">
            <div className="loading-spinner" />
            <p>Đang tải dữ liệu FiveM...</p>
        </div>
    );

    if (!logs || logs.length === 0) return (
        <div className="flt-state">
            <p className="flt-empty-msg">Chưa có log nào</p>
            <p className="flt-empty-sub">Dữ liệu sync từ FiveM mỗi 5 phút · Thử bỏ bộ lọc thời gian</p>
        </div>
    );

    return (
        <div className="flt-wrap">
            <div className="flt-count-bar">
                <span className="flt-count-text">
                    {pagination?.total?.toLocaleString() || logs.length} bản ghi
                    {logs[0]?._fromSnapshot && <span className="flt-snap-badge"> · từ snapshot SQL</span>}
                    {logs[0]?._fromDiscord  && <span className="flt-snap-badge"> · từ Discord realtime</span>}
                </span>
            </div>
            <div className="flt-scroll">
                <table className="flt-table">
                    <thead>
                        <tr>
                            {headers.map((h, i) => <th key={i}>{h}</th>)}
                            <th style={{ width: 32 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {logs.filter(l => !l._collapsed).map((log, i) => (
                            <FiveMLogRow key={log._id || log._key || i} log={log} mode={mode} />
                        ))}
                    </tbody>
                </table>
            </div>

            {pagination && pagination.pages > 1 && (
                <div className="flt-pager">
                    <button className="flt-pg-btn" disabled={pagination.page <= 1}
                        onClick={() => onPageChange(pagination.page - 1)}>← Trước</button>
                    <span className="flt-pg-info">
                        {pagination.page} / {pagination.pages}
                        <span className="flt-pg-total"> ({pagination.total?.toLocaleString()})</span>
                    </span>
                    <button className="flt-pg-btn" disabled={pagination.page >= pagination.pages}
                        onClick={() => onPageChange(pagination.page + 1)}>Sau →</button>
                </div>
            )}
        </div>
    );
};

export default FiveMLogTable;
